import { useMemo } from 'react';

import type { PreviewPriorAuthQueryKeyParams } from '@/entities/rx';
import type { RxDraft } from '@/entities/rx';

/**
 * Project the Rx wizard's draft values into the narrow shape the
 * ``/preview_prior_auth`` endpoint consumes. Returns a stable
 * referential identity as long as the inputs that affect PA posture
 * don't change — so react-query's query-key identity comparison is
 * effectively a structural comparison over just these fields.
 *
 * We intentionally:
 *
 * - Coerce string-typed form fields (quantity, days supply, dispense
 *   unit id) into nullable numbers, returning ``null`` for empty /
 *   un-parseable values so the gate sees "unspecified" instead of a
 *   meaningless 0.
 * - Skip the compound branch for now — compound prescriptions don't
 *   have a single ``DispensableDrugId`` the gate can look up, and the
 *   PA rules we model are per-dispensable-drug. For compound drafts
 *   we return nulls and let the ``usePreviewPriorAuthQuery`` hook's
 *   ``enabled`` gating disable the fetch entirely.
 */
export function usePriorAuthBannerModel(values: RxDraft): PreviewPriorAuthQueryKeyParams {
  const isCoded = values.kind === 'coded';
  const dispensableDrugId = isCoded ? (values.selectedMedication?.DispensableDrugId ?? null) : null;
  const pharmacyId = values.selectedPharmacy?.PharmacyId ?? null;
  const rawQuantity = values.quantity;
  const rawDaysSupply = values.daysSupply;
  const rawDispenseUnitId = isCoded ? values.dispenseUnitId : null;

  return useMemo(
    () => ({
      dispensableDrugId,
      pharmacyId,
      quantity: parseOptionalNumber(rawQuantity),
      daysSupply: parseOptionalNumber(rawDaysSupply),
      dispenseUnitTypeId:
        rawDispenseUnitId === null ? null : parseOptionalNumber(rawDispenseUnitId),
    }),
    [dispensableDrugId, pharmacyId, rawQuantity, rawDaysSupply, rawDispenseUnitId]
  );
}

function parseOptionalNumber(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
